import { createSteeringURL } from '@/api'
import { steeringAuthInit } from '@/stores/auth'
import { useEpisodeStore } from '@/stores/episodes'
import { useMediaStore } from '@/stores/media'
import { useShowStore } from '@/stores/shows'
import { useTimeSlotStore } from '@/stores/timeslots'
import { Episode, ProgramEntry } from '@/types'
import { APIConsume, ensureError, useQuery } from '@/util'
import { PaginatedListOperationOptions } from '@rokoli/bnb'
import { APIListPaginated, createExtendableAPI } from '@rokoli/bnb/drf'
import { computedAsync } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, MaybeRefOrGetter, readonly, ref, toValue, watch } from 'vue'

export const useProgramStore = defineStore('program', () => {
  const endpoint = createSteeringURL.prefix('program', 'basic')
  const { api } = createExtendableAPI<ProgramEntry>(endpoint, steeringAuthInit)
  const listOperations = APIListPaginated(api)
  const consumeOperations = APIConsume(listOperations.listIsolated)

  return {
    list: (options: PaginatedListOperationOptions | undefined = undefined) => {
      return consumeOperations.consume({ ...(options ?? {}), noCache: true })
    },
  }
})

// Checks for continuous program entries to find a problem in the calendar entry generation.
// See: https://gitlab.servus.at/aura/dashboard/-/issues/422#note_28272
function checkContinuous(program: ProgramEntry[]) {
  if (program.length === 0) return

  let _start = program[0].start
  for (const entry of program) {
    console.assert(
      entry.start === _start,
      'Program entry %s starts at %s, but should start at %s for program to be continuous.',
      entry.id,
      entry.start,
      _start,
    )
    _start = entry.end
  }
}

interface UseProgramOptions {
  start: MaybeRefOrGetter<string | Date | undefined | null>
  end: MaybeRefOrGetter<string | Date | undefined | null>
  includeVirtual?: MaybeRefOrGetter<boolean> | undefined
  cutAtRangeBoundaries?: MaybeRefOrGetter<boolean> | undefined
}

export function useProgramSlots(options: UseProgramOptions) {
  const programStore = useProgramStore()
  const timeslotStore = useTimeSlotStore()
  const error = ref<Error | undefined>()
  const isLoading = ref<boolean>(false)
  let controller: AbortController | undefined = undefined
  const _program = ref<ProgramEntry[]>([])
  const program = computed<ProgramEntry[]>(() =>
    query.value.has('start') && query.value.has('end') ? _program.value : [],
  )

  const query = useQuery(() => ({
    start: toValue(options.start),
    end: toValue(options.end),
    includeVirtual: toValue(options?.includeVirtual) ?? true,
    cutAtRangeBoundaries: toValue(options?.cutAtRangeBoundaries) ?? true,
  }))

  async function updateProgram() {
    if (controller) controller.abort()
    error.value = undefined
    controller = new AbortController()
    isLoading.value = true
    try {
      _program.value = await programStore.list({
        query: query.value,
        requestInit: { signal: controller.signal },
      })
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return
      error.value = ensureError(e)
    } finally {
      isLoading.value = false
      controller = undefined
    }
  }

  watch(query, updateProgram, { immediate: true })
  watch(program, (program) => checkContinuous(program))
  watch(_program, (newProgram) => {
    void timeslotStore.retrieveMultiple(
      newProgram.map((entry) => entry.timeslotId).filter((id) => id !== null),
      { useCached: false },
    )
  })

  return {
    program: readonly(program),
    error: readonly(error),
    isLoading: readonly(isLoading),
    reload: updateProgram,
  }
}

export function useExtendedProgram(program: MaybeRefOrGetter<readonly ProgramEntry[]>) {
  const showStore = useShowStore()
  const timeslotStore = useTimeSlotStore()
  const mediaStore = useMediaStore()
  const episodeStore = useEpisodeStore()

  async function warmupCache(program: readonly ProgramEntry[]) {
    await Promise.all([
      await Promise.allSettled([
        showStore.retrieveMultiple(
          program.map((e) => e.showId),
          { useCached: true },
        ),
        timeslotStore.retrieveMultiple(
          program.map((e) => e.timeslotId).filter((id) => id !== null) as number[],
          { useCached: true },
        ),
        mediaStore.retrieveMultiple(
          program.map((e) => e.mediaId).filter((id) => id !== null) as number[],
          { useCached: true },
        ),
      ]),
    ])

    const episodeIds = new Set<Episode['id']>()
    for (const entry of program) {
      if (!entry.timeslotId) continue
      const timeslot = await timeslotStore.retrieve(entry.timeslotId, { useCached: true })
      if (timeslot?.episodeId) episodeIds.add(timeslot.episodeId)
    }

    await episodeStore.retrieveMultiple(Array.from(episodeIds), { useCached: true })
  }

  async function* getExtendedProgramEntries(program: readonly ProgramEntry[]) {
    for (const entry of program) {
      const show = await showStore.retrieve(entry.showId, { useCached: true })
      const media = entry.mediaId
        ? await mediaStore.retrieve(entry.mediaId, { useCached: true })
        : null
      const timeslot = entry.timeslotId
        ? await timeslotStore.retrieve(entry.timeslotId, { useCached: true })
        : null
      const episode = timeslot?.episodeId
        ? await episodeStore.retrieve(timeslot.episodeId, { useCached: true })
        : null
      yield { ...entry, show, media, timeslot, episode }
    }
  }

  async function getProgram(program: readonly ProgramEntry[]) {
    await warmupCache(program)
    return Array.fromAsync(getExtendedProgramEntries(toValue(program)))
  }

  const evaluating = ref(false)

  return {
    program: computedAsync(() => getProgram(toValue(program)), [], { evaluating }),
    isLoading: readonly(evaluating),
  }
}
